import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Fade,
  Backdrop,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Badge,
  Chip,
  Tooltip,
  Button,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';
import notificationService from '../services/notificationService';

interface NotificationCenterProps {
  onMarkAllAsRead?: () => void;
  onClearAll?: () => void;
}

const NotificationItem = memo(({ notification, theme, onMarkAsRead, onItemClick }: any) => {
  const getIconByType = (type: string) => {
    switch (type) {
      case 'vehicle':
        return <CarIcon style={{ color: theme.palette.primary.main }} />;
      case 'expense':
        return <MoneyIcon style={{ color: theme.palette.error.main }} />;
      case 'sale':
        return <ReceiptIcon style={{ color: theme.palette.success.main }} />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);

    if (diffSec < 60) return 'agora mesmo';
    if (diffMin < 60) return `${diffMin} min atrás`;
    if (diffHour < 24) return `${diffHour} ${diffHour === 1 ? 'hora' : 'horas'} atrás`;
    if (diffDay === 1) return 'ontem';
    if (diffDay < 30) return `${diffDay} dias atrás`;

    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div>
      <ListItem
        alignItems="flex-start"
        onClick={() => onItemClick(notification.type)}
        sx={{
          px: 2,
          py: 1.5,
          width: 'calc(100% - 5px)',
          bgcolor: notification.read
            ? 'transparent'
            : theme.palette.mode === 'dark'
              ? 'rgba(63, 136, 197, 0.1)'
              : 'rgba(63, 136, 197, 0.05)',
          transition: 'all 0.25s',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.2)'
              : 'rgba(0,0,0,0.03)',
            transform: 'translateX(5px)',
          },
        }}
      >
        <ListItemAvatar>
          <Avatar>
            {getIconByType(notification.type)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}>
                {notification.title}
              </Typography>
              <Typography variant="caption">
                {formatRelativeTime(new Date(notification.date))}
              </Typography>
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  {notification.message}
                </Typography>
              </Box>
              {!notification.read && (
                <IconButton size="small" onClick={(e) => onMarkAsRead(notification.id, e)}>
                  <CheckCircleIcon fontSize="small" color="success" />
                </IconButton>
              )}
            </Box>
          }
        />
      </ListItem>
      <Divider component="li" />
    </div>
  );
});

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  onMarkAllAsRead,
  onClearAll
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.getAll();
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleMarkAsRead = useCallback(async (id: number, event: any) => {
    event.stopPropagation();
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      if (onMarkAllAsRead) onMarkAllAsRead();
    } catch (error) {
      console.error(error);
    }
  }, [onMarkAllAsRead]);

  const handleClearAll = useCallback(() => {
    setNotifications([]);
    if (onClearAll) onClearAll();
  }, [onClearAll]);

  const handleNotificationClick = useCallback((type: string) => {
    setOpen(false);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <Tooltip title="Notificações" arrow>
        <IconButton color="inherit" onClick={handleToggle}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Modal open={open} onClose={handleToggle} closeAfterTransition slots={{ backdrop: Backdrop }}>
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 450 },
              maxHeight: 500,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
              bgcolor: 'background.paper'
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NotificationsActiveIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Notificações</Typography>
                {unreadCount > 0 && (
                  <Chip size="small" label={unreadCount} color="error" sx={{ ml: 1 }} />
                )}
              </Box>
              <IconButton size="small" onClick={handleToggle}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
              {loading ? (
                <Box p={3}>
                  <Typography>Carregando...</Typography>
                </Box>
              ) : notifications.length > 0 ? (
                <List disablePadding>
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      theme={theme}
                      onMarkAsRead={handleMarkAsRead}
                      onItemClick={handleNotificationClick}
                    />
                  ))}
                </List>
              ) : (
                <Box p={4} textAlign="center">
                  <Typography>Não há notificações disponíveis</Typography>
                </Box>
              )}
            </Box>

            {notifications.length > 0 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0}
                >
                  Marcar todas como lidas
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleClearAll}
                >
                  Limpar tudo
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default NotificationCenter;
